/**
 * The dashboard (F10).
 *
 * Four questions at a glance: where startups sit (the funnel), what is late
 * (overdue actions), what is in flight (active pilots) and what just happened
 * (recent activity). The four headline figures are `Stat` tiles, where the
 * number IS the datum and so carries the green (§4.2); everywhere else the
 * counts sit as text beside their labels, never colour alone (§9.7).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 7.15, 7.16, PRD F10, §4.2
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { Dashboard } from '@nexus/contracts';

import { STAGE_COLOR } from '../../components/domain/stages';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Empty } from '../../components/ui/Empty';
import { FunnelRow } from '../../components/ui/FunnelRow';
import { Grid } from '../../components/ui/Grid';
import { Kicker } from '../../components/ui/Kicker';
import { PageHead } from '../../components/ui/PageHead';
import { Stat } from '../../components/ui/Stat';
import { useDashboard } from '../../lib/dashboard.queries';
import { formatDate, formatNumber } from '../../lib/format';
import { startupPath, pilotPath } from '../../lib/routes';

export function DashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, isError } = useDashboard();

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <PageHead
        kicker={t('nav.group.pipeline')}
        title={t('page.dashboard')}
        lead={t('dashboard.lead')}
      />

      {isPending ? (
        <p className="text-body" aria-busy="true">
          {t('state.loading')}
        </p>
      ) : (
        <DashboardContent data={data} />
      )}
    </>
  );
}

function DashboardContent({ data }: { data: Dashboard }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-s4">
      <Grid cols={4}>
        <Card>
          <Stat
            label={t('dashboard.totals.startups')}
            value={formatNumber(data.totals.startups)}
          />
        </Card>
        <Card>
          <Stat
            label={t('dashboard.totals.activePilots')}
            value={formatNumber(data.totals.activePilots)}
          />
        </Card>
        <Card>
          <Stat
            label={t('dashboard.totals.overdueActions')}
            value={formatNumber(data.totals.overdueActions)}
          />
        </Card>
        <Card>
          <Stat
            label={t('dashboard.totals.stalledStartups')}
            value={formatNumber(data.totals.stalledStartups)}
          />
        </Card>
      </Grid>

      <FunnelCard funnel={data.funnel} />

      <Grid cols={2}>
        <OverdueCard rows={data.overdueActions} />
        <ActivePilotsCard rows={data.activePilots} />
      </Grid>

      <RecentActivityCard rows={data.recentActivity} />
    </div>
  );
}

function FunnelCard({ funnel }: { funnel: Dashboard['funnel'] }): React.JSX.Element {
  const { t } = useTranslation();
  const max = funnel.reduce((m, stage) => Math.max(m, stage.count), 0);

  return (
    <Card>
      <CardHead>
        <Kicker as="h2">{t('dashboard.funnel.title')}</Kicker>
      </CardHead>
      <CardBody>
        {funnel.map((stage) => (
          <FunnelRow
            key={stage.stage}
            label={t(`stage.${stage.stage}`)}
            count={stage.count}
            max={max}
            color={STAGE_COLOR[stage.stage]}
          />
        ))}
      </CardBody>
    </Card>
  );
}

function OverdueCard({ rows }: { rows: Dashboard['overdueActions'] }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHead>
        <Kicker as="h2" count={rows.length}>
          {t('dashboard.overdue.title')}
        </Kicker>
      </CardHead>
      <CardBody flush>
        {rows.length === 0 ? (
          <Empty title={t('dashboard.overdue.emptyTitle')}>
            {t('dashboard.overdue.emptyBody')}
          </Empty>
        ) : (
          <ul>
            {rows.map((row) => (
              <li key={row.id} className="border-gray-100 border-b last:border-b-0">
                <Link
                  to={startupPath(row.id)}
                  className="hover:bg-off-white px-s3 flex items-baseline justify-between gap-3 py-2.5"
                >
                  <span className="min-w-0">
                    <span className="text-navy-text text-sm font-medium">{row.name}</span>
                    {row.nextAction !== null && (
                      <span className="text-body block truncate text-[13px] font-light">
                        {row.nextAction}
                      </span>
                    )}
                  </span>
                  {row.nextActionDueDate !== null && (
                    <span className="text-sem-bad flex-none text-xs tabular-nums">
                      {formatDate(row.nextActionDueDate)}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ActivePilotsCard({
  rows,
}: {
  rows: Dashboard['activePilots'];
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHead>
        <Kicker as="h2" count={rows.length}>
          {t('dashboard.activePilots.title')}
        </Kicker>
      </CardHead>
      <CardBody flush>
        {rows.length === 0 ? (
          <Empty title={t('dashboard.activePilots.emptyTitle')}>
            {t('dashboard.activePilots.emptyBody')}
          </Empty>
        ) : (
          <ul>
            {rows.map((row) => (
              <li key={row.id} className="border-gray-100 border-b last:border-b-0">
                <Link
                  to={pilotPath(row.id)}
                  className="hover:bg-off-white px-s3 flex items-baseline justify-between gap-3 py-2.5"
                >
                  <span className="min-w-0">
                    <span className="text-navy-text text-sm font-medium">
                      {row.title}
                    </span>
                    <span className="text-body block truncate text-[13px] font-light">
                      {row.customerName === null
                        ? row.startupName
                        : `${row.startupName} · ${row.customerName}`}
                    </span>
                  </span>
                  <span className="text-body flex-none text-xs">
                    {t(`pilot.status.${row.status}`)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function RecentActivityCard({
  rows,
}: {
  rows: Dashboard['recentActivity'];
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHead>
        <Kicker as="h2">{t('dashboard.recentActivity.title')}</Kicker>
      </CardHead>
      <CardBody flush>
        {rows.length === 0 ? (
          <Empty title={t('dashboard.recentActivity.emptyTitle')}>
            {t('dashboard.recentActivity.emptyBody')}
          </Empty>
        ) : (
          <ul>
            {rows.map((row) => (
              <li
                key={row.id}
                className="border-gray-100 px-s3 flex items-baseline justify-between gap-3 border-b py-2.5 last:border-b-0"
              >
                <span className="min-w-0">
                  <span className="text-navy-text text-sm">{row.summary}</span>
                  <span className="text-body block text-[13px] font-light">
                    {row.authorName ?? t('activity.unknownAuthor')}
                  </span>
                </span>
                <span className="text-body flex-none text-xs tabular-nums">
                  {formatDate(row.occurredAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
